import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
// Components
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import GlobalLoader from '../../../components/main-loading/global-loader';
import MyLink from '../../../components/mylink';
import FastTabs, { TabsDatas } from '../../../components/tabs/tabs';
// Ressources
import envConfig from '../../../config';
import CoreAPIService from '../../../utils/core-api';
import DateUtils from '../../../utils/dateUtils';
import FormattingUtils from '../../../utils/formattingUtils';
import { createPath, paths } from '../../paths';

type TopPeriod = 'week' | 'month';

const PERIODS: TopPeriod[] = ['week', 'month'];

type TopMetric = 'income' | 'rides' | 'rating';

const METRICS: TopMetric[] = ['income', 'rides', 'rating'];

// Must match DRIVER_TOP_MIN_RATINGS in ride-manager, shown in the rating tab's hint.
const MIN_RATINGS = 5;

const MEDALS = ['🥇', '🥈', '🥉'];

interface DriverTopRow {
    rank: number;
    driver_uuid: string;
    id: number | null;
    firstname: string;
    lastname: string;
    control_tower_name: string | null;
    rides_count: number;
    total_income: number;
    avg_rating: number | null;
    ratings_count: number;
}

interface DriverTopPayload {
    period: TopPeriod;
    since: number;
    until: number;
    income: DriverTopRow[];
    rides: DriverTopRow[];
    rating: DriverTopRow[];
}

const useStyles = makeStyles((theme) => ({
    toolbar: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    tableContainer: {
        overflowX: 'auto',
    },
    rankCell: {
        width: 56,
        fontSize: 18,
        textAlign: 'center',
    },
    // The figure the tab ranks on, set apart from the secondary ones
    highlight: {
        fontWeight: 600,
        color: theme.palette.secondary.dark,
    },
}));

const formatRating = (value: number | null): string => (value !== null && value !== undefined ? value.toFixed(2) : '-');

interface TopPanelProps {
    t: any;
    classes: Record<string, string>;
    metric: TopMetric;
    rows: DriverTopRow[];
}

const TopPanel = ({ t, classes, metric, rows }: TopPanelProps): JSX.Element => {
    if (!rows.length) {
        return <Typography variant="body2">{t('top.empty')}</Typography>;
    }

    const cell = (column: TopMetric, content: React.ReactNode): JSX.Element => (
        <TableCell align="right" className={column === metric ? classes.highlight : undefined}>
            {content}
        </TableCell>
    );

    return (
        <div className={classes.tableContainer}>
            {metric === 'income' && (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    {t('top.income_hint')}
                </Typography>
            )}
            {metric === 'rating' && (
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    {t('top.rating_hint', { count: MIN_RATINGS })}
                </Typography>
            )}
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell className={classes.rankCell}>#</TableCell>
                        <TableCell>{t('top.columns.driver')}</TableCell>
                        <TableCell>{t('top.columns.control_tower')}</TableCell>
                        {cell('income', t('top.columns.income'))}
                        {cell('rides', t('top.columns.rides'))}
                        {cell('rating', t('top.columns.rating'))}
                        <TableCell align="right">{t('top.columns.ratings_count')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => {
                        const name = `${row.firstname} ${row.lastname}`.trim() || row.driver_uuid;
                        return (
                            <TableRow key={row.driver_uuid}>
                                <TableCell className={classes.rankCell}>{MEDALS[row.rank - 1] ?? row.rank}</TableCell>
                                <TableCell>
                                    {row.id !== null ? (
                                        <MyLink color="secondary" to={createPath(paths.driversDetails, { id: row.id })}>
                                            {name}
                                        </MyLink>
                                    ) : (
                                        name
                                    )}
                                </TableCell>
                                <TableCell>{row.control_tower_name || '-'}</TableCell>
                                {cell('income', FormattingUtils.price(row.total_income, true))}
                                {cell('rides', row.rides_count)}
                                {cell('rating', formatRating(row.avg_rating))}
                                <TableCell align="right">{row.ratings_count}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </div>
    );
};

interface DriversTopPageProps extends WithTranslation {}

const DriversTopPage = ({ t }: DriversTopPageProps): JSX.Element => {
    const classes = useStyles();

    const [period, setPeriod] = React.useState<TopPeriod>('week');
    const [byPeriod, setByPeriod] = React.useState<Partial<Record<TopPeriod, DriverTopPayload>>>({});
    const [errors, setErrors] = React.useState<Partial<Record<TopPeriod, any>>>({});

    // Periods with a request in flight, so a quick back-and-forth does not fetch twice.
    const pendingRef = React.useRef<Set<TopPeriod>>(new Set());

    // One call per period serves the three rankings; kept once loaded, so switching back is instant.
    React.useEffect(() => {
        if (byPeriod[period] || errors[period] || pendingRef.current.has(period)) {
            return;
        }
        pendingRef.current.add(period);
        CoreAPIService.get(`internal/backoffice/drivers/top?period=${period}`, null, null, envConfig.privapi_url)
            .then((res: any) => setByPeriod((prev) => ({ ...prev, [period]: res.payload })))
            .catch((err) => setErrors((prev) => ({ ...prev, [period]: err })))
            .finally(() => pendingRef.current.delete(period));
    }, [period, byPeriod, errors]);

    const datas = byPeriod[period];
    const error = errors[period];

    const renderContent = (): JSX.Element => {
        if (error) {
            return <ErrorMessage payload={error} />;
        }
        if (!datas) {
            return <GlobalLoader center />;
        }
        const tabs: TabsDatas = {
            storageKey: 'drivers-top-tab',
            datas: METRICS.map((metric) => ({
                label: t(`top.tabs.${metric}`),
                children: <TopPanel t={t} classes={classes} metric={metric} rows={datas[metric] ?? []} />,
            })),
        };
        return <FastTabs tabs={tabs} />;
    };

    return (
        <App
            header={{
                title: t('top.title'),
                subtitle: t('top.description'),
            }}
        >
            <div className={classes.toolbar}>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={period}
                    onChange={(event, value: TopPeriod | null) => value && setPeriod(value)}
                >
                    {PERIODS.map((p) => (
                        <ToggleButton key={p} value={p}>
                            {t(`top.periods.${p}`)}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                {datas && (
                    <Typography variant="body2" color="textSecondary">
                        {t('top.window', {
                            since: DateUtils.tsToDate(datas.since),
                            until: DateUtils.tsToDate(datas.until),
                        })}
                    </Typography>
                )}
            </div>
            {renderContent()}
        </App>
    );
};

export default withTranslation(['driver', 'common'])(DriversTopPage);
